/**
 * Text Extraction Parser - One-Step Extraction for CSV/Excel
 *
 * Parses player data from one-step text extraction (CSV/Excel files).
 * No assessment phase needed - structured files already have columns defined.
 *
 * Ported from mobile/utils/text-extraction-parser.ts for full feature parity.
 */

import type { ParsedCSV, StructureAnalysis, ExtractTextResponse, ExtractedPlayerRow } from '../types/extraction';
import { parseRawPlayerData } from './pipe-parser';
import type { ExtractedRow } from "../types/extraction";

// =============================================================================
// Text Extraction Parser
// =============================================================================

/**
 * Parse player data from text extraction response
 * @param response - ExtractTextResponse from API
 * @returns ParsedCSV for field-mapping screen
 */
export function parsePlayerData(response: ExtractTextResponse): ParsedCSV {
  // Split into lines
  const lines = response.players.trim().split('\n');

  if (lines.length === 0) {
    throw new Error('No player data found');
  }

  // Determine headers and data lines
  let headers: string[];
  let dataLines: string[];

  // Check if API provided valid column names (not data)
  const hasValidColumns = response.columns && response.columns.length > 0 && isValidColumnNames(response.columns);

  if (hasValidColumns && response.columns) {
    // Headers provided by API
    headers = response.columns;

    // Check if first line is a duplicate header row (matches column names)
    const firstLine = lines[0].split('|').map(h => h.trim().toLowerCase());
    const columnNamesLower = response.columns.map(c => c.toLowerCase());
    const isFirstLineHeader = firstLine.length === columnNamesLower.length &&
      firstLine.every((val, idx) => val === columnNamesLower[idx]);

    if (isFirstLineHeader) {
      // Skip duplicate header row
      dataLines = lines.slice(1);
    } else {
      // No duplicate header, all lines are data
      dataLines = lines;
    }
  } else {
    // Backend returned invalid columns or none - extract from first line
    // First line should be player data in format: number|name|name|year|position|school|email
    // We need to infer column names based on the data structure
    const firstRow = lines[0].split('|').map(h => h.trim());

    if (firstRow.length === 7) {
      // Standard roster format: number|first_name|last_name|grad_year|position|school|email
      headers = ['number', 'first_name', 'last_name', 'grad_year', 'position', 'school', 'email'];
    } else {
      // Fallback - try to infer from data patterns
      headers = inferColumnNames(firstRow);
    }

    dataLines = lines; // All lines are data
  }

  // Auto-detect roster type if not provided or invalid
  const validRosterTypes = ['single_team', 'showcase'];
  const detectedRosterType = (response.rosterType && validRosterTypes.includes(response.rosterType))
    ? response.rosterType
    : detectRosterType(headers);

  // Create minimal structure analysis for parser
  const structure: StructureAnalysis = {
    format: 'table',
    roster_type: detectedRosterType,
    team_indicator: 'explicit_column',
    estimated_player_count: dataLines.length,
    columns: headers,
    teams: null,
  };

  // Parse player data using existing pipe parser
  const players = parseRawPlayerData(dataLines.join('\n'), headers, structure);

  // Build ParsedCSV from player data
  const parsedData = buildParsedCSV(players, detectedRosterType);

  return parsedData;
}

/**
 * Check if columns are valid column names (not data)
 */
function isValidColumnNames(columns: string[]): boolean {
  // Every target name the API's column-mapping prompt is allowed to produce, plus the
  // spellings the older image-extraction path emits. This list has to stay a superset of
  // the prompt's STANDARD TARGET COLUMN NAMES in
  // `RosterExtraction:CsvColumnMappingPrompt` (api/RosterPilotAPI/appsettings.json).
  // A name the API can send but this list has never heard of reads as "the backend sent me
  // data instead of headers", and the fallback below then relabels the columns positionally
  // as number|first_name|last_name|grad_year|position|school|email. On a mailing-address
  // roster (First Name, Last Name, Address, City, State, Zipcode) that put the street
  // address in last_name for every player, silently, with no error anywhere.
  const validNames = [
    // Identity
    'number', 'jersey', 'first_name', 'last_name', 'name_first', 'name_last',
    'position', 'pos', 'grad_year', 'year', 'class', 'school', 'email', 'phone',
    'commitment', 'age', 'dob', 'birth_date',
    // Team
    'team_id', 'team', 'team_color', 'team_number',
    // Physical / academic
    'bats', 'throws', 'height', 'weight', 'gpa', 'sat', 'act',
    // Social
    'twitter', 'instagram',
    // Location
    'address', 'hometown', 'city', 'st', 'state', 'zipcode', 'zip', 'postal_code',
    // People
    'parent_name', 'parent_phone', 'coach_name', 'coach_phone',
    'advisor_name', 'advisor_phone',
    // Free text
    'notes', 'note', 'comments',
  ];

  // Two is enough. A roster is identified by its name columns; a mailing list carries little
  // else this list can recognize, and guessing the shape of one is strictly worse than
  // trusting two real headers. Column names that are actually data match none of these.
  const validCount = columns.filter(col =>
    validNames.includes(col.trim().toLowerCase())
  ).length;

  return validCount >= 2;
}

/**
 * Infer column names from first data row patterns
 */
function inferColumnNames(firstRow: string[]): string[] {
  // Standard format is: number|first_name|last_name|grad_year|position|school|email
  if (firstRow.length >= 5) {
    const headers = ['number', 'first_name', 'last_name', 'grad_year', 'position'];

    // Add remaining columns
    if (firstRow.length > 5) headers.push('school');
    if (firstRow.length > 6) headers.push('email');
    if (firstRow.length > 7) headers.push('notes');

    return headers;
  }

  // Fallback - generic column names
  return firstRow.map((_, i) => `column_${i + 1}`);
}

/**
 * Auto-detect roster type from column headers
 */
function detectRosterType(headers: string[]): 'single_team' | 'showcase' {
  const lowerHeaders = headers.map(h => h.toLowerCase());

  // If has team/color columns, it's a showcase
  if (
    lowerHeaders.includes('team_id') ||
    lowerHeaders.includes('team_color') ||
    lowerHeaders.includes('team')
  ) {
    return 'showcase';
  }

  return 'single_team';
}

/**
 * Build ParsedCSV from extracted player rows.
 * Constructs the ParsedCSV object directly (no CSV serialize/deserialize round-trip)
 * to avoid data corruption from unescaped commas in free-text fields like notes.
 */
function buildParsedCSV(
  players: ExtractedPlayerRow[],
  rosterType: 'single_team' | 'showcase'
): ParsedCSV {
  if (players.length === 0) {
    throw new Error('No players extracted from response');
  }

  // Build headers based on roster type
  const headers: string[] = [];

  if (rosterType === 'showcase') {
    headers.push('team_id', 'team_color');
  }
  headers.push('number', 'last_name', 'first_name');
  headers.push('position', 'bats', 'throws', 'grad_year', 'school', 'email', 'notes');

  // Also include stats/contact fields that the pipe-parser extracted
  const statFields = ['height', 'weight', 'gpa', 'sat', 'act'];
  const contactFields = ['phone', 'twitter', 'instagram', 'city', 'state', 'address', 'zip',
    'parent_name', 'parent_phone', 'coach_name', 'coach_phone', 'advisor_name', 'advisor_phone'];
  const extraFields = [...statFields, ...contactFields];

  // Check which extra fields have data in any player
  const activeExtraFields: string[] = [];
  for (const field of extraFields) {
    const csvKey = field === 'height' ? 'ht' : field === 'weight' ? 'wt' : field;
    if (players.some(p => {
      const val = getStatOrContactValue(p, csvKey);
      return val !== undefined && val !== '-' && val !== '';
    })) {
      activeExtraFields.push(field);
      headers.push(field);
    }
  }

  // Build rows as ExtractedRow
  const clean = (v: string) => v === '-' ? '' : v;

  const rows: ExtractedRow[] = players.map(player => {
    const row: ExtractedRow = {};

    if (rosterType === 'showcase') {
      row.team_id = clean(player.team_id);
      row.team_color = clean(player.team_color);
    }

    row.number = clean(player.number);
    row.last_name = clean(player.name_last);
    row.first_name = clean(player.name_first);
    row.position = clean(player.position);
    row.bats = clean(player.bats);
    row.throws = clean(player.throws);
    // The raw Class cell, not the normalized year - same reason as
    // extraction-response-parser.ts: once the cell is a four-digit year the class it named is
    // gone, and with it the JC year the review step reads off it.
    row.grad_year = clean(player.class);
    row.school = clean(player.school);
    row.email = clean(player.email);
    row.notes = clean(player.notes);

    // Add extra fields that have data
    for (const field of activeExtraFields) {
      const csvKey = field === 'height' ? 'ht' : field === 'weight' ? 'wt' : field;
      const val = getStatOrContactValue(player, csvKey);
      row[field] = val && val !== '-' ? val : '';
    }

    return row;
  });

  return {
    headers,
    rows,
    rowCount: rows.length,
    columnCount: headers.length,
    rosterType,
  };
}

/**
 * Extract a value from stats_csv or contact_csv by key.
 * stats_csv format: "ht:6-1;wt:180;gpa:3.8"
 * contact_csv format: "phone:555-1234;twitter:@handle"
 */
function getStatOrContactValue(player: ExtractedPlayerRow, key: string): string | undefined {
  for (const csv of [player.stats_csv, player.contact_csv]) {
    if (!csv || csv === '-') continue;
    for (const pair of csv.split(';')) {
      const colonIdx = pair.indexOf(':');
      if (colonIdx > 0 && pair.substring(0, colonIdx).trim() === key) {
        return pair.substring(colonIdx + 1).trim();
      }
    }
  }
  return undefined;
}
